import { type B2bConfig, createB2bCustomer, type CreateB2bCustomerInput } from '@api/b2b';
import { listWarehousesForPicker } from '@api/work-order';
import { Button, Modal, NumberInput, Select, TagsInput, TextInput } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import styles from './b2b.module.css';
import { errorMessage } from './utils';

const blankAddress = { street: '', city: '', state: '', zip: '' };

/** Adding a B2B customer (B2B Customers page, or Settings → B2B Customers). Opens their account page after, unless `onCreated` is given. */
const CreateCustomerModal: React.FC<{ config: B2bConfig; onClose: () => void; onCreated?: (customerId: string) => void }> = ({ config, onClose, onCreated }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { data: warehouses = [] } = useQuery({ queryKey: ['warehouses-picker-internal'], queryFn: listWarehousesForPicker, staleTime: 5 * 60 * 1000 });
    const [input, setInput] = useState<CreateB2bCustomerInput>({
        name: '',
        segment: config.segments[0]?.key ?? '',
        email: '',
        billing: { ...blankAddress },
        shipTos: [{ name: '', ...blankAddress }],
        tags: [],
    });
    const [sameAsBilling, setSameAsBilling] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const set = (patch: Partial<CreateB2bCustomerInput>) => setInput((x) => ({ ...x, ...patch }));
    const setBilling = (patch: Partial<CreateB2bCustomerInput['billing']>) => set({ billing: { ...input.billing, ...patch } });
    const setShipTo = (patch: Partial<CreateB2bCustomerInput['shipTos'][number]>) => set({ shipTos: [{ ...input.shipTos[0]!, ...patch }] });

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            const clean = (v?: string) => (v && v.trim() ? v.trim() : undefined);
            const shipTo = sameAsBilling ? { name: input.shipTos[0]?.name || input.name, ...input.billing } : input.shipTos[0]!;
            const created = await createB2bCustomer({
                ...input,
                legalName: clean(input.legalName),
                gstin: clean(input.gstin)?.toUpperCase(),
                phone: clean(input.phone),
                shipFromWarehouseId: clean(input.shipFromWarehouseId),
                shipTos: [{ ...shipTo, gstin: clean(shipTo.gstin) }],
                tags: input.tags?.length ? input.tags : undefined,
                minOrderValue: input.minOrderValue || undefined,
            });
            await queryClient.invalidateQueries({ queryKey: ['b2b-customers'] });
            if (onCreated) onCreated(created.customerId);
            else void navigate(`/b2b/customers/${encodeURIComponent(created.customerId)}`);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const ready = input.name.trim() && input.email.trim() && input.segment && input.billing.street && input.billing.city && input.billing.state && input.billing.zip;

    return (
        <Modal opened onClose={onClose} title="Add B2B customer" centered size="lg">
            <div className={styles.stack}>
                <div className={styles.fieldGrid}>
                    <TextInput label="Name" required value={input.name} onChange={(e) => set({ name: e.currentTarget.value })} />
                    <TextInput label="Legal name" value={input.legalName ?? ''} onChange={(e) => set({ legalName: e.currentTarget.value })} />
                    <TextInput label="GSTIN" value={input.gstin ?? ''} onChange={(e) => set({ gstin: e.currentTarget.value })} />
                    <Select label="Segment" required data={config.segments.map((s) => ({ value: s.key, label: s.label }))} value={input.segment} onChange={(v) => set({ segment: v ?? '' })} />
                    <TextInput label="Email (PIs and dispatch updates)" required value={input.email} onChange={(e) => set({ email: e.currentTarget.value })} />
                    <TextInput label="Phone" value={input.phone ?? ''} onChange={(e) => set({ phone: e.currentTarget.value })} />
                    <TagsInput label="Tags" value={input.tags ?? []} onChange={(v) => set({ tags: v })} />
                    <NumberInput label="Minimum order value" min={0} value={input.minOrderValue ?? ''} onChange={(v) => set({ minOrderValue: v === '' ? undefined : Number(v) })} />
                    <Select
                        label="Ships from"
                        data={warehouses.map((w) => ({ value: w.warehouseId, label: w.name }))}
                        value={input.shipFromWarehouseId ?? null}
                        onChange={(v) => set({ shipFromWarehouseId: v ?? undefined })}
                        placeholder={config.defaultShipFromWarehouseId ? 'Brand default' : 'Choose at dispatch'}
                        clearable
                    />
                </div>
                <span className={styles.cardTitle}>Billing address</span>
                <div className={styles.fieldGrid}>
                    <TextInput label="Street" required value={input.billing.street} onChange={(e) => setBilling({ street: e.currentTarget.value })} />
                    <TextInput label="City" required value={input.billing.city} onChange={(e) => setBilling({ city: e.currentTarget.value })} />
                    <TextInput label="State" required value={input.billing.state} onChange={(e) => setBilling({ state: e.currentTarget.value })} />
                    <TextInput label="PIN code" required value={input.billing.zip} onChange={(e) => setBilling({ zip: e.currentTarget.value })} />
                </div>
                <Select
                    label="Ship to"
                    data={[
                        { value: 'same', label: 'The billing address' },
                        { value: 'other', label: 'Another address' },
                    ]}
                    value={sameAsBilling ? 'same' : 'other'}
                    onChange={(v) => setSameAsBilling(v !== 'other')}
                />
                {!sameAsBilling ? (
                    <div className={styles.fieldGrid}>
                        <TextInput label="Location name" value={input.shipTos[0]?.name ?? ''} onChange={(e) => setShipTo({ name: e.currentTarget.value })} />
                        <TextInput label="GSTIN, if registered separately" value={input.shipTos[0]?.gstin ?? ''} onChange={(e) => setShipTo({ gstin: e.currentTarget.value })} />
                        <TextInput label="Street" value={input.shipTos[0]?.street ?? ''} onChange={(e) => setShipTo({ street: e.currentTarget.value })} />
                        <TextInput label="City" value={input.shipTos[0]?.city ?? ''} onChange={(e) => setShipTo({ city: e.currentTarget.value })} />
                        <TextInput label="State" value={input.shipTos[0]?.state ?? ''} onChange={(e) => setShipTo({ state: e.currentTarget.value })} />
                        <TextInput label="PIN code" value={input.shipTos[0]?.zip ?? ''} onChange={(e) => setShipTo({ zip: e.currentTarget.value })} />
                    </div>
                ) : null}
                <span className={styles.muted}>Payment terms come from the segment ({config.segments.find((s) => s.key === input.segment)?.label ?? '—'}); 100% advance when the segment sets none.</span>
                {error ? <span className={styles.error}>{error}</span> : null}
                <Button variant={ButtonVariant.ACTION} disabled={!ready} loading={busy} onClick={() => void save()} w="fit-content">
                    Add customer
                </Button>
            </div>
        </Modal>
    );
};

export default CreateCustomerModal;
